// Copy button with success morph (green flash + scale-pop) and the
// assistant-avatar streaming pulse CSS. Both belong to the same
// "Chat polish v1" set so they share one CSS-injecting module.
//
// Pattern matches ThinkingIndicator.jsx + ToolActivityStrip.jsx:
//   * One-time <style id="..."> inject on module load
//   * Memoized React component using data-* attributes for state
//   * @media (prefers-reduced-motion: reduce) disables the animation
//
// Why a wrapper component instead of inline useState in each call
// site: there are 4 copy-button sites in app.jsx today (more likely
// to follow). Each needs its own copied/cleared state independent
// of the others; a shared hook can't be called inside a .map()
// without a component boundary.

import { useState, useRef } from "react";

const STYLE_ID = "chat-polish-style";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
/* Copy-button success morph: green flash + brief scale pop. The
   color override uses !important so it beats inline hover handlers
   that mutate currentTarget.style.color (a pattern used at one of
   the call sites for theme-accent hover). */
@keyframes cf-copy-success-pop {
  0%   { transform: scale(1); }
  35%  { transform: scale(1.22); }
  100% { transform: scale(1); }
}
.cf-copy-btn { transition: color 200ms ease, border-color 200ms ease; }
.cf-copy-btn[data-copied="true"] {
  color: #22c55e !important;
  border-color: rgba(34,197,94,0.45) !important;
  animation: cf-copy-success-pop 380ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
/* Item 9: clipboard.writeText rejection flips the button red for the
   same 1.2s window. Same scale-pop animation so the affordance feels
   uniform with the success case -- only the color changes. */
.cf-copy-btn[data-copied="failed"] {
  color: #ef4444 !important;
  border-color: rgba(239,68,68,0.45) !important;
  animation: cf-copy-success-pop 380ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

/* Assistant-avatar pulse while a message is streaming. Soft radial
   glow that breathes outward 1.6s in / out. Bound to a per-message
   data attribute (only the in-flight bubble pulses, not all of
   them) so the page never has more than one pulse active. */
@keyframes cf-avatar-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(91,163,201,0.0); }
  50%      { box-shadow: 0 0 0 6px rgba(91,163,201,0.18); }
}
[data-cf-avatar-pulse="true"] {
  animation: cf-avatar-pulse 1.6s ease-in-out infinite;
  border-radius: 9px;
}

/* Chat polish v2: hover-revealed code-block action buttons.
   The Preview / Copy chips inside a code-block header sit at
   opacity 0.45 (still visible to touch users + screen readers)
   and animate to 1 when the parent block is hovered OR has
   focus-within (keyboard nav). */
.cf-code-block .cf-code-actions {
  opacity: 0.45;
  transition: opacity 180ms ease;
}
.cf-code-block:hover .cf-code-actions,
.cf-code-block:focus-within .cf-code-actions {
  opacity: 1;
}

/* Chat polish v2: sidebar chat-row entrance stagger. Each row
   slides in 6px from the left + fades over 220ms. animationDelay
   is set inline on the row based on its index in the list (capped
   at 10 rows of delay = 250ms total so a 200-chat list doesn't
   take 5 seconds to settle). React keys keep rows stable across
   re-renders so the animation only fires on initial mount. */
@keyframes cf-sidebar-row-enter {
  from { opacity: 0; transform: translateX(-6px); }
  to   { opacity: 1; transform: translateX(0); }
}
.cf-sidebar-row {
  animation: cf-sidebar-row-enter 220ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

/* cf-shimmer: loading skeleton primitive. A neutral block with a
   gradient sweep moving left -> right. Use as <div class="cf-shimmer"
   style={{ height:N, width:'M%', borderRadius:6 }} /> for whatever
   placeholder dimensions you need. */
@keyframes cf-shimmer-sweep {
  0%   { background-position: -240px 0; }
  100% { background-position: 240px 0; }
}
.cf-shimmer {
  background: linear-gradient(90deg, rgba(127,127,127,0.06) 0%, rgba(127,127,127,0.16) 50%, rgba(127,127,127,0.06) 100%);
  background-size: 240px 100%;
  background-repeat: no-repeat;
  background-color: rgba(127,127,127,0.08);
  animation: cf-shimmer-sweep 1.4s ease-in-out infinite;
  border-radius: 6px;
  display: block;
}

/* cf-page-fade-in: small mount fade for any page surface. */
@keyframes cf-page-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.cf-page-fade-in { animation: cf-page-fade-in 340ms cubic-bezier(0.16, 1, 0.3, 1) both; }

/* cf-composer-focus: focus-ring affordance for chat composer
   textareas. Subtle accent ring + slight border tint. */
.cf-composer-focus { transition: border-color 180ms ease, box-shadow 180ms ease; }
.cf-composer-focus:focus, .cf-composer-focus:focus-within {
  outline: none;
  border-color: rgba(91, 163, 201, 0.55) !important;
  box-shadow: 0 0 0 3px rgba(91, 163, 201, 0.14);
}

/* cf-sidebar-slide: nav-row hover translate-x reveal. */
.cf-sidebar-slide { transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1), background 180ms ease; }
.cf-sidebar-slide:hover { transform: translateX(3px); }

/* === Whole-app polish wave: 20 reusable primitives === */

/* 1. cf-focus: universal accent focus ring. Use on inputs / buttons
   / selects that need a visible keyboard-focus indicator. */
.cf-focus:focus-visible, .cf-focus:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(91, 163, 201, 0.32), 0 0 0 1px rgba(91, 163, 201, 0.7);
}

/* 2. Accent text selection across the whole app. */
::selection { background: rgba(91, 163, 201, 0.32); color: inherit; }

/* 3. cf-keyhint: small Cmd/Ctrl/etc. shortcut badge. */
.cf-keyhint {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(127, 127, 127, 0.18);
  border: 1px solid rgba(127, 127, 127, 0.22);
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 10px;
  font-weight: 700;
  color: inherit;
  line-height: 1.4;
  vertical-align: middle;
}

/* 4. cf-scroll-shadow: inset top + bottom shadows to hint at
   scrollable content. Apply to any scroll container. Uses sticky
   linear gradients on ::before / ::after pseudos. */
.cf-scroll-shadow { position: relative; }
.cf-scroll-shadow::before, .cf-scroll-shadow::after {
  content: ""; position: sticky; left: 0; right: 0;
  display: block; height: 16px; pointer-events: none; z-index: 1;
}
.cf-scroll-shadow::before { top: 0;    background: linear-gradient(180deg, rgba(0,0,0,0.10), transparent); margin-bottom: -16px; }
.cf-scroll-shadow::after  { bottom: 0; background: linear-gradient(0deg,   rgba(0,0,0,0.10), transparent); margin-top: -16px; }

/* 5. cf-skeleton-line: text-line variant of cf-shimmer with sane
   default height + bottom margin. */
.cf-skeleton-line {
  height: 12px;
  margin-bottom: 8px;
  background: linear-gradient(90deg, rgba(127,127,127,0.06) 0%, rgba(127,127,127,0.16) 50%, rgba(127,127,127,0.06) 100%);
  background-size: 240px 100%;
  background-repeat: no-repeat;
  background-color: rgba(127,127,127,0.08);
  animation: cf-shimmer-sweep 1.4s ease-in-out infinite;
  border-radius: 4px;
}

/* 6. cf-slide-up-modal: alt entrance for modals that should slide
   up from the bottom (mobile-like sheet feel). */
@keyframes cf-slide-up-modal {
  from { opacity: 0; transform: translateY(24px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0)    scale(1); }
}
.cf-slide-up-modal { animation: cf-slide-up-modal 360ms cubic-bezier(0.16, 1, 0.3, 1) both; }

/* 7. cf-tooltip: CSS-only tooltip via data-tooltip attribute. */
.cf-tooltip { position: relative; }
.cf-tooltip[data-tooltip]:hover::after,
.cf-tooltip[data-tooltip]:focus-visible::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  padding: 5px 10px;
  background: rgba(20, 20, 28, 0.94);
  color: #fff;
  font-size: 11px;
  font-family: inherit;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}

/* 8. cf-link-arrow: appends an arrow on hover for outbound links. */
.cf-link-arrow { position: relative; }
.cf-link-arrow::after {
  content: " →";
  display: inline-block;
  margin-left: 2px;
  opacity: 0.6;
  transition: transform 200ms ease, opacity 200ms ease;
}
.cf-link-arrow:hover::after { transform: translateX(3px); opacity: 1; }

/* 9. cf-text-balance: balanced wrapping for headings (browser-
   dependent fallback OK). */
.cf-text-balance { text-wrap: balance; -webkit-text-wrap: balance; }

/* 10. Clean print rules. Hide nav/sidebar/topbar/modals/composers;
    leave the main reading area visible. */
@media print {
  .cf-rail, .cf-topbar-pill, [data-cf-no-print="true"],
  .cf-modal-backdrop, .cf-modal-panel,
  .cf-toast, .cf-toast-action,
  .cf-jump-pill, .cf-newchat-btn {
    display: none !important;
  }
  body { background: white !important; color: black !important; }
  a { color: black !important; text-decoration: underline; }
}

/* 11. Smooth scroll for in-page anchor jumps. Disabled under
    reduced-motion via the existing :root override. */
html { scroll-behavior: smooth; }

/* 12. Consistent disabled treatment on buttons app-wide. */
button:disabled, button[disabled] {
  cursor: not-allowed !important;
  opacity: 0.55;
}

/* 13. cf-link-underline: subtle accent underline on hover. */
.cf-link-underline { text-decoration: none; transition: color 180ms ease; position: relative; }
.cf-link-underline::after {
  content: ""; position: absolute; left: 0; right: 100%; bottom: -2px;
  height: 1px; background: currentColor; transition: right 200ms cubic-bezier(0.16, 1, 0.3, 1);
}
.cf-link-underline:hover::after { right: 0; }

/* 14. cf-card-hover: tiny lift hover for any generic card. */
.cf-card-hover { transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 200ms ease; }
.cf-card-hover:hover { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(0, 0, 0, 0.10); }

/* 15. cf-divider: subtle 1px gradient divider for section breaks. */
.cf-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(127, 127, 127, 0.22), transparent);
  margin: 16px 0;
}

/* 16. cf-fade-mask: fades the rightmost 24px so overflow text
    blends instead of getting hard-cut by ellipsis. */
.cf-fade-mask {
  -webkit-mask-image: linear-gradient(90deg, black calc(100% - 24px), transparent 100%);
          mask-image: linear-gradient(90deg, black calc(100% - 24px), transparent 100%);
}

/* 17. cf-loading-dots: classic three-bouncing-dots. */
@keyframes cf-loading-dot { 0%, 80%, 100% { opacity: 0.25; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }
.cf-loading-dots { display: inline-flex; gap: 4px; vertical-align: middle; }
.cf-loading-dots span {
  width: 6px; height: 6px; border-radius: 50%; background: currentColor;
  animation: cf-loading-dot 1.2s ease-in-out infinite;
}
.cf-loading-dots span:nth-child(2) { animation-delay: 0.16s; }
.cf-loading-dots span:nth-child(3) { animation-delay: 0.32s; }

/* 18. cf-input-focus-glow: softer ambient glow for input focus
    (use INSTEAD of cf-focus when you want a calmer effect). */
.cf-input-focus-glow:focus { outline: none; box-shadow: 0 0 0 4px rgba(91, 163, 201, 0.12); transition: box-shadow 200ms ease; }

/* 19. cf-row-zebra: zebra striping for any row list. */
.cf-row-zebra > *:nth-child(even) { background: rgba(127, 127, 127, 0.03); }

/* 20. cf-grow-x: small horizontal grow on hover (for short text
    chips / labels). */
.cf-grow-x { transition: transform 180ms cubic-bezier(0.16, 1, 0.3, 1); transform-origin: left center; }
.cf-grow-x:hover { transform: scaleX(1.04); }

/* === polish wave 2 -- 10 more primitives === */

/* 1. cf-pulse-dot: breathing colored dot for "live" indicators.
   Color via currentColor or background. */
@keyframes cf-pulse-dot {
  0%, 100% { opacity: 0.5; transform: scale(0.9); }
  50%      { opacity: 1;   transform: scale(1.15); }
}
.cf-pulse-dot {
  display: inline-block;
  width: 7px; height: 7px;
  border-radius: 50%;
  background: currentColor;
  animation: cf-pulse-dot 1.6s ease-in-out infinite;
}

/* 2. cf-badge-new: small accent "NEW" pill. */
.cf-badge-new {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 700;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  letter-spacing: 0.05em;
  background: rgba(91, 163, 201, 0.18);
  color: rgb(91, 163, 201);
  border: 1px solid rgba(91, 163, 201, 0.4);
  text-transform: uppercase;
  vertical-align: middle;
}

/* 3. cf-pill-active: alt active state for nav pills -- subtle gradient
   background instead of the flat accent tint. Apply with .cf-pill-active. */
.cf-pill-active {
  background: linear-gradient(135deg, rgba(91, 163, 201, 0.22), rgba(91, 163, 201, 0.08));
  border-color: rgba(91, 163, 201, 0.5) !important;
}

/* 4. cf-divider-vert: thin vertical divider line. */
.cf-divider-vert {
  display: inline-block;
  width: 1px;
  height: 16px;
  background: rgba(127, 127, 127, 0.22);
  margin: 0 8px;
  vertical-align: middle;
}

/* 5. cf-skel-card: card-shaped skeleton placeholder. */
.cf-skel-card {
  height: 64px;
  border-radius: 12px;
  background: linear-gradient(90deg, rgba(127,127,127,0.06) 0%, rgba(127,127,127,0.16) 50%, rgba(127,127,127,0.06) 100%);
  background-size: 240px 100%;
  background-repeat: no-repeat;
  background-color: rgba(127,127,127,0.08);
  animation: cf-shimmer-sweep 1.4s ease-in-out infinite;
}

/* 6. cf-fade-edges: linear-gradient fade mask on both sides of a
   horizontal scroller -- so chips fade into the page edges. */
.cf-fade-edges {
  -webkit-mask-image: linear-gradient(90deg, transparent 0, black 24px, black calc(100% - 24px), transparent 100%);
          mask-image: linear-gradient(90deg, transparent 0, black 24px, black calc(100% - 24px), transparent 100%);
}

/* 7. cf-link-external: appends ↗ icon for outbound links. */
.cf-link-external::after {
  content: " ↗";
  display: inline-block;
  margin-left: 2px;
  opacity: 0.6;
  font-size: 0.85em;
}

/* 8. cf-noise: subtle noise SVG background overlay -- adds a tactile
   feel to large empty areas. Layer it on top of background colors. */
.cf-noise {
  position: relative;
}
.cf-noise::after {
  content: "";
  position: absolute; inset: 0;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='1'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.06 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
  opacity: 0.6;
  pointer-events: none;
  mix-blend-mode: multiply;
}

/* 9. cf-glow-hover: accent box-shadow glow on hover. */
.cf-glow-hover { transition: box-shadow 200ms ease; }
.cf-glow-hover:hover { box-shadow: 0 0 0 4px rgba(91, 163, 201, 0.12), 0 8px 24px rgba(91, 163, 201, 0.18); }

/* 10. cf-text-gradient: gradient-filled text primitive. */
.cf-text-gradient {
  background-image: linear-gradient(135deg, rgb(91, 163, 201), rgb(184, 224, 240));
  -webkit-background-clip: text;
          background-clip: text;
  -webkit-text-fill-color: transparent;
          color: transparent;
}

/* === end polish wave 2 === */

/* === end whole-app polish wave === */

/* cf-section-enter: section/page entrance fade-up (longer + bigger
   than page-fade-in, for hero content). */
@keyframes cf-section-enter {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
.cf-section-enter { animation: cf-section-enter 480ms cubic-bezier(0.16, 1, 0.3, 1) both; }

/* Toast dismiss button hover. */
.cf-toast-dismiss { transition: opacity 160ms ease, color 160ms ease; opacity: 0.6; }
.cf-toast-dismiss:hover { opacity: 1; }

/* Workflow card polish: lift on hover, pulse when running, star
   toggle bounce, slide-in entrance. */
@keyframes cf-wf-card-enter {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.cf-wf-card {
  animation: cf-wf-card-enter 360ms cubic-bezier(0.16, 1, 0.3, 1) both;
  transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 200ms ease, border-color 200ms ease;
}
.cf-wf-card:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(0, 0, 0, 0.12); }
@keyframes cf-wf-card-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(91, 163, 201, 0.4), 0 4px 16px rgba(0, 0, 0, 0.08); }
  50%      { box-shadow: 0 0 0 8px rgba(91, 163, 201, 0), 0 4px 16px rgba(0, 0, 0, 0.08); }
}
.cf-wf-card-pulse { animation: cf-wf-card-pulse 1.6s ease-in-out infinite; }
.cf-wf-star { transition: transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1), color 180ms ease; }
.cf-wf-star:hover { transform: scale(1.2) rotate(8deg); }
.cf-wf-star[data-pinned="true"] { color: #f59e0b; }

/* Workflow run-detail polish: pulse on the active step, fade-up
   entrance on completed steps, smooth progress-bar fill. */
@keyframes cf-wf-step-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(91, 163, 201, 0.35); }
  50%      { box-shadow: 0 0 0 6px rgba(91, 163, 201, 0); }
}
.cf-wf-step-pulse { animation: cf-wf-step-pulse 1.6s ease-in-out infinite; }
@keyframes cf-wf-step-enter {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.cf-wf-step-enter { animation: cf-wf-step-enter 320ms cubic-bezier(0.16, 1, 0.3, 1) both; }
.cf-wf-progress-bar > div { transition: width 380ms cubic-bezier(0.16, 1, 0.3, 1); }

/* Misc polish primitives:
   * cf-btn-lift: subtle hover lift + shadow for any CTA button.
   * cf-plan-chip: hover scale + brightness for the topbar plan chip.
   * cf-empty-icon-pop: subtle pop entrance for big emoji icons
     in cf-empty-state cards. */
.cf-btn-lift { transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease; }
.cf-btn-lift:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
  filter: brightness(1.04);
}
.cf-btn-lift:not(:disabled):active { transform: translateY(0); }
.cf-plan-chip { transition: transform 180ms ease, filter 180ms ease; }
.cf-plan-chip:hover { transform: scale(1.04); filter: brightness(1.1); }
@keyframes cf-empty-icon-pop {
  0%   { opacity: 0; transform: scale(0.5) rotate(-8deg); }
  60%  { opacity: 1; transform: scale(1.1) rotate(4deg); }
  100% { opacity: 0.6; transform: scale(1) rotate(0deg); }
}
.cf-empty-icon-pop {
  display: inline-block;
  animation: cf-empty-icon-pop 720ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

/* Owner Console polish: row hover + expanded slide-down + empty
   state. */
.cf-owner-row { transition: background 140ms ease; }
.cf-owner-row:hover { background: rgba(127, 127, 127, 0.05); }
@keyframes cf-owner-expand-in {
  from { opacity: 0; transform: translateY(-4px); max-height: 0; }
  to   { opacity: 1; transform: translateY(0); max-height: 1200px; }
}
.cf-owner-expand {
  animation: cf-owner-expand-in 320ms cubic-bezier(0.16, 1, 0.3, 1) both;
  overflow: hidden;
}
@keyframes cf-owner-empty-in {
  from { opacity: 0; transform: scale(0.985); }
  to   { opacity: 1; transform: scale(1); }
}
.cf-owner-empty {
  animation: cf-owner-empty-in 380ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

/* Pricing page polish: card hover lift + add-on hover affordance
   + toggle active ring + header entrance. */
@keyframes cf-pricing-header-enter {
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.cf-pricing-header {
  animation: cf-pricing-header-enter 520ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
.cf-pricing-card { transition: transform 220ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 220ms ease, border-color 220ms ease; }
.cf-pricing-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
}
.cf-pricing-addon { transition: transform 180ms ease, border-color 180ms ease, background 180ms ease; }
.cf-pricing-addon:hover { transform: translateY(-2px); }
.cf-pricing-toggle { transition: transform 180ms ease, box-shadow 180ms ease; }
.cf-pricing-toggle[data-active="true"] { transform: scale(1.02); }
.cf-pricing-cta { transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease; }
.cf-pricing-cta:hover { transform: translateY(-1px); box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18); filter: brightness(1.05); }

/* Audit row hover + zebra striping. Subtle highlight reinforces
   which row the cursor is on -- crucial in a dense log view. */
.cf-audit-row { transition: background 140ms ease; }
.cf-audit-row:nth-child(even) { background: rgba(127, 127, 127, 0.025); }
.cf-audit-row:hover { background: rgba(127, 127, 127, 0.08); }

/* cf-upgrade-banner: shared 402 plan-gate banner primitive.
   Subtle scale-in entrance + accent-tinted border. Use anywhere
   a server 402 needs a friendly upgrade prompt. */
@keyframes cf-upgrade-banner-enter {
  from { opacity: 0; transform: translateY(-4px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
.cf-upgrade-banner {
  animation: cf-upgrade-banner-enter 360ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

/* Login + Sign-up polish: auth panel entrance + hover lifts. */
@keyframes cf-auth-panel-enter {
  from { opacity: 0; transform: translateY(8px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
.cf-auth-panel {
  animation: cf-auth-panel-enter 440ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
.cf-auth-submit {
  transition: transform 180ms ease, box-shadow 180ms ease, filter 180ms ease;
}
.cf-auth-submit:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.22);
  filter: brightness(1.05);
}
.cf-auth-submit:not(:disabled):active { transform: translateY(0); }
.cf-auth-tab { transition: background 160ms ease, color 160ms ease; }
.cf-auth-tab:hover { background: rgba(127, 127, 127, 0.08); }
.cf-auth-sso { transition: transform 180ms ease, border-color 180ms ease, background 180ms ease; }
.cf-auth-sso:hover {
  transform: translateY(-1px);
}
@keyframes cf-auth-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.cf-auth-spinner svg { animation: cf-auth-spin 720ms linear infinite; }

/* Chat polish v3a: empty-state hero entrance. Logo scales in
   from 0.7 + fades; title/desc/cards fade-up. Staggered so the
   eye lands on the logo, then the headline, then the starters.
   Starter cards also get a subtle hover lift via transform. */
@keyframes cf-hero-logo-enter {
  from { opacity: 0; transform: scale(0.7) translateY(-12px); }
  to   { opacity: 1; transform: scale(1)   translateY(0); }
}
@keyframes cf-hero-text-enter {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
/* Chat polish v3+: hero logo continuous breathing animation. After
   the entrance completes (480ms), the logo enters a gentle pulse:
   scale 1.0 -> 1.025 + soft 8px accent box-shadow ring every 4s.
   Inner icon (AppLogo child) floats 2px vertically on the same
   period so it feels suspended inside the breathing ring. Hover
   pauses the breathing and locks a brighter state. */
@keyframes cf-hero-logo-breathe {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(91, 163, 201, 0);
  }
  50% {
    transform: scale(1.025);
    box-shadow: 0 0 0 8px rgba(91, 163, 201, 0.08);
  }
}
@keyframes cf-hero-icon-float {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-2px); }
}
.cf-hero-logo {
  animation:
    cf-hero-logo-enter 480ms cubic-bezier(0.16, 1, 0.3, 1) both,
    cf-hero-logo-breathe 4s ease-in-out 480ms infinite;
  /* GPU compositing -- the scale in cf-hero-logo-breathe re-rasterizes
     the logo at sub-pixel positions each frame without these hints,
     which looks blurry on the SVG. will-change promotes the element
     to its own compositor layer; the GPU then scales the cached
     bitmap instead of re-rasterizing. */
  will-change: transform;
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
}
.cf-hero-logo > * {
  animation: cf-hero-icon-float 4s ease-in-out 480ms infinite;
  will-change: transform;
  backface-visibility: hidden;
}
.cf-hero-logo:hover {
  animation-play-state: paused;
  transform: scale(1.04);
  box-shadow: 0 0 0 10px rgba(91, 163, 201, 0.18);
  transition: transform 240ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 240ms ease;
}
.cf-hero-logo:hover > * {
  animation-play-state: paused;
  transform: translateY(0);
}
/* Pause the electricity on hover too so the user gets a clean,
   static logo when they're focused on it. */
.cf-hero-logo:hover::before, .cf-hero-logo:hover::after {
  animation-play-state: paused;
  opacity: 0;
}

/* Chat polish v27: realistic Tesla-coil electricity around the
   hero logo. Drawn via inline SVG inside the .cf-hero-logo div
   (see app.jsx). 8 lightning paths radiate from the rounded
   square's edges; each path animates stroke-dashoffset 200 -> 0
   to "draw on" during the heartbeat peak, then fades opacity to
   0. Per-path animation-delay creates a chaotic strike window
   centered on the 4s heartbeat peak (480ms entrance delay +
   ~2000ms = peak at 2480ms). */
.cf-hero-logo {
  position: relative;
  overflow: visible; /* bolts radiate OUTSIDE the rounded square */
}
/* Bright burst at the center on the strike beat -- this is the
   "light up" the user wants to keep. Pulses synced with the 4s
   heartbeat. */
.cf-hero-logo::before {
  content: "";
  position: absolute;
  inset: -8px;
  border-radius: 26px;
  pointer-events: none;
  background: radial-gradient(circle at 50% 50%, rgba(255,255,255,0.9) 0%, rgba(184,224,240,0.55) 30%, rgba(91,163,201,0) 65%);
  opacity: 0;
  animation: cf-hero-burst 4s ease-out 480ms infinite;
}
@keyframes cf-hero-burst {
  0%, 100% { opacity: 0; transform: scale(0.85); }
  44%      { opacity: 0; transform: scale(0.85); }
  50%      { opacity: 0.7; transform: scale(1); }
  58%      { opacity: 0;   transform: scale(1.15); }
}

.cf-hero-title { animation: cf-hero-text-enter 360ms ease-out  80ms both; }
.cf-hero-desc  { animation: cf-hero-text-enter 360ms ease-out 160ms both; }
.cf-hero-starter {
  /* 3D flip-in -- the card pivots from -60deg around the Y axis into
     place over 480ms. Pairs with the per-swap remount in app.jsx so
     only the slot whose content changed plays the animation; the
     other 3 cards stay still. */
  animation: cf-hero-starter-flip 720ms cubic-bezier(0.16, 1, 0.3, 1) both;
  transform-origin: center center;
  backface-visibility: hidden;
  will-change: transform, opacity;
  transition: border-color 150ms ease, transform 220ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 220ms ease;
}
.cf-hero-starter:hover {
  transform: translateY(-2px) scale(1.015);
  box-shadow: 0 6px 18px rgba(91, 163, 201, 0.18);
}
@keyframes cf-hero-starter-flip {
  0%   { opacity: 0; transform: perspective(640px) rotateY(-58deg) translateY(4px); }
  55%  { opacity: 1; transform: perspective(640px) rotateY(12deg)  translateY(0); }
  100% { opacity: 1; transform: perspective(640px) rotateY(0deg)   translateY(0); }
}

/* Chat polish v3b: streaming-bubble shimmer line. ::after pseudo
   draws a 2px gradient line at the bottom of the streaming text
   div. Pairs with the avatar pulse from v1 -- two visual cues
   for the streaming state, not just one. */
@keyframes cf-stream-shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
.cf-stream-accent {
  position: relative;
}
.cf-stream-accent::after {
  content: "";
  position: absolute;
  left: 0; right: 0; bottom: -6px;
  height: 2px;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(91,163,201,0.55) 50%,
    transparent 100%);
  background-size: 200% 100%;
  animation: cf-stream-shimmer 1.6s linear infinite;
  border-radius: 1px;
  pointer-events: none;
}

/* Chat polish v3c: InputBar focus glow. Soft outer ring + accent
   border when the input shell (or any descendant) has focus. The
   transition is forced via !important to beat the inline
   transition: "all 0.2s" that already lives on the same element. */
.cf-input-shell {
  transition: border-color 200ms ease, box-shadow 220ms ease !important;
}
.cf-input-shell:focus-within {
  border-color: #5ba3c9 !important;
  box-shadow: 0 0 0 4px rgba(91, 163, 201, 0.13), 0 2px 16px rgba(0,0,0,0.07) !important;
}

/* Chat polish v4a: inline link hover underline grow. Default underline
   is the existing text-decoration; on hover, add a slightly thicker
   text-decoration-thickness that animates. Browsers that don't support
   text-decoration-thickness fall back to the static underline. */
.cf-md-link {
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  transition: text-decoration-thickness 160ms ease, text-underline-offset 160ms ease;
}
.cf-md-link:hover {
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}

/* Chat polish v4b: table row hover. Subtle accent-tinted bg on
   each <tr> hover -- helps the eye track across wide tables. */
.cf-md-table tbody tr {
  transition: background 140ms ease;
}
.cf-md-table tbody tr:hover {
  background: rgba(91, 163, 201, 0.08);
}

/* Chat polish v4c: blockquote accent. Existing default has no border;
   add an accent-colored left bar + padding + slight italic for the
   classic quote look. */
.cf-md-blockquote {
  border-left: 3px solid rgba(91, 163, 201, 0.55);
  background: rgba(91, 163, 201, 0.05);
  padding: 6px 12px 6px 14px;
  margin: 8px 0;
  border-radius: 0 6px 6px 0;
  font-style: italic;
  color: inherit;
  opacity: 0.92;
}

/* Chat polish v4d: HR gradient. Replaces the flat hairline with a
   soft horizontal gradient that fades out at both ends -- visual
   separation that doesn't feel like a hard cut. */
.cf-md-hr {
  border: 0 !important;
  height: 1px;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(127,127,127,0.35) 50%,
    transparent 100%);
  margin: 16px 0;
}

/* Chat polish v4e: code block language colored dot. Tiny 8px circle
   before the language label, colored per common language. Hex codes
   match the broad community palette (yellow=js, blue=ts, etc.). */
.cf-lang-dot {
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  background: #9aa0a6; /* default grey */
  vertical-align: middle;
}
.cf-lang-dot[data-lang="js"], .cf-lang-dot[data-lang="javascript"], .cf-lang-dot[data-lang="jsx"] { background: #f7df1e; }
.cf-lang-dot[data-lang="ts"], .cf-lang-dot[data-lang="typescript"], .cf-lang-dot[data-lang="tsx"] { background: #3178c6; }
.cf-lang-dot[data-lang="py"], .cf-lang-dot[data-lang="python"]      { background: #3776ab; }
.cf-lang-dot[data-lang="rb"], .cf-lang-dot[data-lang="ruby"]        { background: #cc342d; }
.cf-lang-dot[data-lang="go"]                                        { background: #00add8; }
.cf-lang-dot[data-lang="rs"], .cf-lang-dot[data-lang="rust"]        { background: #dea584; }
.cf-lang-dot[data-lang="java"]                                      { background: #b07219; }
.cf-lang-dot[data-lang="c"], .cf-lang-dot[data-lang="cpp"], .cf-lang-dot[data-lang="c++"] { background: #f34b7d; }
.cf-lang-dot[data-lang="cs"], .cf-lang-dot[data-lang="csharp"]      { background: #178600; }
.cf-lang-dot[data-lang="php"]                                       { background: #4f5d95; }
.cf-lang-dot[data-lang="sh"], .cf-lang-dot[data-lang="bash"], .cf-lang-dot[data-lang="zsh"] { background: #89e051; }
.cf-lang-dot[data-lang="html"]                                      { background: #e34c26; }
.cf-lang-dot[data-lang="css"]                                       { background: #563d7c; }
.cf-lang-dot[data-lang="json"]                                      { background: #d4af37; }
.cf-lang-dot[data-lang="yaml"], .cf-lang-dot[data-lang="yml"]       { background: #cb171e; }
.cf-lang-dot[data-lang="md"], .cf-lang-dot[data-lang="markdown"]    { background: #083fa1; }
.cf-lang-dot[data-lang="sql"]                                       { background: #e38c00; }

/* Chat polish v5a: new-chat (+) button. Hover scale + accent glow.
   Caller's inline style already transitions "all" but we use a
   class so the hover state is declarative. */
.cf-newchat-btn {
  transition: transform 160ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 200ms ease;
}
.cf-newchat-btn:hover {
  transform: scale(1.08);
  box-shadow: 0 0 0 3px rgba(91, 163, 201, 0.18);
}

/* Chat polish v5b: active sidebar row accent bar. A 2px accent-colored
   line on the left edge of the currently-active chat row. Pure CSS via
   ::before; only fires when data-active="true". */
.cf-sidebar-row {
  position: relative;
}
.cf-sidebar-row[data-active="true"]::before {
  content: "";
  position: absolute;
  left: -1px;
  top: 6px;
  bottom: 6px;
  width: 2px;
  background: #5ba3c9;
  border-radius: 1px;
}

/* Chat polish v5c: sidebar section header hover bg. Subtle accent
   tint when hovering the "Chats" / "Workspaces" / etc. header row. */
.cf-sidebar-header {
  transition: background 140ms ease;
  border-radius: 6px;
}
.cf-sidebar-header:hover {
  background: rgba(91, 163, 201, 0.05);
}

/* Chat polish v5d: sidebar row icon micro-bounce on hover. The agent
   emoji / brand-logo square does a tiny scale-bump when the parent
   row is hovered. */
.cf-sidebar-row:hover .cf-sidebar-icon {
  transform: scale(1.12);
}
.cf-sidebar-icon {
  transition: transform 220ms cubic-bezier(0.16, 1, 0.3, 1);
}

/* Chat polish v5e: brand logo hover micro-tilt. The header brand
   logo block (top of main column when sidebar is collapsed) tilts
   3deg + scales 1.05 on hover. */
.cf-brand-logo {
  transition: transform 280ms cubic-bezier(0.16, 1, 0.3, 1);
  transform-origin: center;
}
.cf-brand-logo:hover {
  transform: rotate(-3deg) scale(1.05);
}

/* Chat polish v6a: provider badge hover scale. Provider name pill
   (e.g. "Anthropic Claude Opus 4.7") gets a small scale-up + cursor
   help on hover so it feels interactive (it isn't clickable but the
   user gets feedback that it's a distinct affordance). */
.cf-provider-badge {
  transition: transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
  cursor: help;
}
.cf-provider-badge:hover {
  transform: scale(1.06);
}

/* Chat polish v6b: tool-call chip "done" settle. After a chip
   transitions to status="done", do a brief 1.0 -> 1.05 -> 1.0 pulse
   so the eye catches the completion. Only fires on done-state. */
@keyframes cf-tool-chip-done-settle {
  0%   { transform: scale(1); }
  40%  { transform: scale(1.05); }
  100% { transform: scale(1); }
}
.cf-tool-chip[data-status="done"] {
  animation: cf-tool-chip-done-settle 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

/* Chat polish v6c: user-avatar hover micro-bounce. The square user
   avatar (initials or photo) does a 1.0 -> 1.08 -> 1.0 spring on
   hover. The assistant avatar already has the streaming pulse so
   this targets only the user one. */
.cf-user-avatar {
  transition: transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1);
  display: inline-block;
}
.cf-user-avatar:hover {
  transform: scale(1.08);
}

/* Chat polish v6d: SearchSources chip hover lift. Each source pill
   in the SearchSources strip gets a translateY(-1px) + accent border
   on hover. */
.cf-source-chip {
  transition: transform 180ms cubic-bezier(0.16, 1, 0.3, 1), border-color 180ms ease;
}
.cf-source-chip:hover {
  transform: translateY(-1px);
  border-color: rgba(91, 163, 201, 0.55) !important;
}

/* Chat polish v7a: attachment chip entrance. Each file chip slides
   in 4px from the bottom + fades over 220ms when first mounted. */
@keyframes cf-attach-chip-enter {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.cf-attach-chip {
  animation: cf-attach-chip-enter 220ms cubic-bezier(0.16, 1, 0.3, 1) both;
  transition: transform 180ms cubic-bezier(0.16, 1, 0.3, 1), border-color 180ms ease;
}
.cf-attach-chip:hover {
  transform: scale(1.04);
}

/* Chat polish v7b: send button hover. Scale to 1.08 + accent glow.
   When busy (showing spinner) the hover effect is suppressed via
   data-busy="true". */
.cf-send-btn {
  transition: transform 160ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 220ms ease;
}
.cf-send-btn:hover:not([data-busy="true"]) {
  transform: scale(1.08);
  box-shadow: 0 0 0 4px rgba(91, 163, 201, 0.18);
}

/* Chat polish v7c: mic button. Pulsing red dot when dictating; hover
   scale when idle. Uses data-dictating="true" gating so only one
   state is active at a time. */
@keyframes cf-mic-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.35); }
  50%      { box-shadow: 0 0 0 6px rgba(220,38,38,0.0); }
}
.cf-mic-btn {
  transition: transform 160ms cubic-bezier(0.16, 1, 0.3, 1);
}
.cf-mic-btn[data-dictating="true"] {
  animation: cf-mic-pulse 1.4s ease-in-out infinite;
}
.cf-mic-btn:hover:not([data-dictating="true"]) {
  transform: scale(1.08);
}

/* Chat polish v8: toast notification polish */

/* v8a: container slide-up + scale-in entrance */
@keyframes cf-toast-enter {
  from { opacity: 0; transform: translateX(-50%) translateY(16px) scale(0.92); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0)    scale(1); }
}
.cf-toast {
  animation: cf-toast-enter 320ms cubic-bezier(0.16, 1, 0.3, 1) both;
  position: relative;
  overflow: hidden;
}

/* v8b: progress bar countdown across the bottom edge. width animates
   from 100% to 0 over the toast's lifetime. Two variants -- 2.2s for
   plain string toasts, 6s for action toasts (the {text, actionLabel,
   onAction} shape). data-action="true" picks the longer variant. */
@keyframes cf-toast-progress {
  from { width: 100%; }
  to   { width: 0%; }
}
.cf-toast::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 100%;
  background: rgba(91, 163, 201, 0.55);
  animation: cf-toast-progress 2200ms linear forwards;
  border-bottom-left-radius: 24px;
}
.cf-toast[data-action="true"]::after {
  animation-duration: 6000ms;
  background: rgba(245, 158, 11, 0.55);  /* warm amber for action toasts */
}

/* v8c: toast dot subtle pulse so the user notices the toast even if
   they were looking away when it appeared. */
@keyframes cf-toast-dot-pulse {
  0%, 100% { transform: scale(1);   opacity: 1; }
  50%      { transform: scale(1.35); opacity: 0.7; }
}
.cf-toast-dot {
  animation: cf-toast-dot-pulse 1.4s ease-in-out infinite;
}

/* v8d: action-toast button hover affordance */
.cf-toast-action {
  transition: transform 160ms cubic-bezier(0.16, 1, 0.3, 1), color 140ms ease;
}
.cf-toast-action:hover {
  transform: translateX(2px);
}

/* v8e: subtle stagger -- the toast text fades in slightly after the
   container settles, so the eye lands on the dot first, then text. */
@keyframes cf-toast-text-enter {
  from { opacity: 0; }
  to   { opacity: 1; }
}
.cf-toast-text {
  animation: cf-toast-text-enter 220ms ease-out 120ms both;
}

/* Chat polish v9: scroll polish */

/* v9a: universal thin scrollbar (webkit + firefox). Applies to
   anything inside the app. Theme-aware via currentColor fallback. */
* {
  scrollbar-width: thin;
  scrollbar-color: rgba(127,127,127,0.35) transparent;
}
*::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
*::-webkit-scrollbar-track {
  background: transparent;
}
*::-webkit-scrollbar-thumb {
  background: rgba(127,127,127,0.28);
  border-radius: 4px;
  transition: background 180ms ease;
}
*::-webkit-scrollbar-thumb:hover {
  background: rgba(91, 163, 201, 0.55);
}
*::-webkit-scrollbar-corner {
  background: transparent;
}

/* v9b: scroll-to-bottom pill bounce-in. The pill is always mounted
   (opacity 0 when hidden); animate the data-visible="true" state. */
@keyframes cf-pill-enter {
  from { opacity: 0; transform: translateY(8px) scale(0.9); }
  to   { opacity: 1; transform: translateY(0)   scale(1); }
}
.cf-jump-pill[data-visible="true"] {
  animation: cf-pill-enter 280ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

/* v9c: pill hover scale */
.cf-jump-pill {
  transition: transform 180ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 220ms ease;
}
.cf-jump-pill[data-visible="true"]:hover {
  transform: scale(1.06);
  box-shadow: 0 6px 18px rgba(0,0,0,0.32);
}

/* v9d: pill chevron icon micro-bounce on hover */
@keyframes cf-pill-chevron-bounce {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(2px); }
}
.cf-jump-pill[data-visible="true"]:hover svg {
  animation: cf-pill-chevron-bounce 0.7s ease-in-out infinite;
}

/* v9e: smooth scroll for the main chat scroller container. The
   useStickyBottom hook already handles imperative scroll; this just
   makes manual user scrolls (page-up / home / etc.) smooth. */
.cf-chat-scroller {
  scroll-behavior: smooth;
}

/* Chat polish v10: markdown extras */

/* v10a: inline <code> hover. Background brightens on hover so the
   eye can pick out distinct code spans in a paragraph. */
.cf-md-inline-code {
  transition: background 160ms ease;
}
.cf-md-inline-code:hover {
  background: rgba(91, 163, 201, 0.15) !important;
}

/* v10b: list bullet/number accent color. ::marker styling has solid
   browser support in 2024+; targets ul + ol consistently. */
.cf-md-list li::marker {
  color: rgba(91, 163, 201, 0.85);
  font-weight: 700;
}

/* v10c: headings hover bottom-border accent. Wraps the existing
   markdown heading element to give it a soft underline that grows
   in on hover. */
.cf-md-heading {
  position: relative;
  padding-bottom: 2px;
}
.cf-md-heading::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  width: 0;
  height: 2px;
  background: rgba(91, 163, 201, 0.55);
  border-radius: 1px;
  transition: width 280ms cubic-bezier(0.16, 1, 0.3, 1);
}
.cf-md-heading:hover::after {
  width: 40px;
}

/* v10d: markdown image hover scale. Useful for embedded screenshots
   in chat -- click-to-open behavior is unchanged, but the hover
   gives a "this is interactive" hint. */
.cf-md-img {
  transition: transform 240ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 240ms ease;
  cursor: zoom-in;
}
.cf-md-img:hover {
  transform: scale(1.015);
  box-shadow: 0 4px 16px rgba(0,0,0,0.18);
}

/* v10e: heading anchor link icon. Reveal on heading hover. The
   icon is decorative; future PR can wire it to copy a deep link. */
.cf-md-heading-anchor {
  opacity: 0;
  margin-left: 8px;
  font-size: 0.8em;
  transition: opacity 180ms ease, transform 180ms ease;
  transform: translateX(-4px);
  display: inline-block;
}
.cf-md-heading:hover .cf-md-heading-anchor {
  opacity: 0.55;
  transform: translateX(0);
}

/* Chat polish v11: form + button polish */

/* v11a: color picker swatch hover -- ring grows in on hover. The
   selected swatch already shows scale(1.2) inline; we layer a hover
   ring underneath for non-selected swatches. */
.cf-color-swatch {
  position: relative;
  transition: transform 160ms cubic-bezier(0.16, 1, 0.3, 1);
}
.cf-color-swatch:hover {
  transform: scale(1.12);
}

/* v11b: emoji picker tile hover scale. The existing tile is a small
   square with cursor:pointer; bump scale on hover. */
.cf-emoji-tile {
  transition: transform 160ms cubic-bezier(0.16, 1, 0.3, 1), border-color 140ms ease;
}
.cf-emoji-tile:hover {
  transform: scale(1.12);
}

/* v11c: spinner glyph refresh. The send button's busy state uses
   IC.spin (an SVG spinner). Wrap it in cf-spinner-icon so we can
   apply a smoother rotation curve + theme-aware color. */
@keyframes cf-spinner-rotate {
  from { transform: rotate(0); }
  to   { transform: rotate(360deg); }
}
.cf-spinner-icon {
  animation: cf-spinner-rotate 0.8s linear infinite;
  display: inline-block;
}

/* v11d: disabled button striped pattern. Any element with
   data-disabled="true" or [disabled] gets a subtle striped overlay
   so the disabled state is visible without color contrast. */
button[disabled],
button[data-disabled="true"] {
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 6px,
    rgba(127,127,127,0.07) 6px,
    rgba(127,127,127,0.07) 12px
  );
  cursor: not-allowed !important;
  opacity: 0.6;
}

/* v11e: universal :focus-visible ring. Keyboard nav gets a clear
   accent-blue ring on any focusable element. Mouse clicks don't
   trigger :focus-visible so no spurious rings on mouse users. */
button:focus-visible,
a:focus-visible,
[role="button"]:focus-visible,
input:focus-visible,
textarea:focus-visible,
select:focus-visible {
  outline: 2px solid rgba(91, 163, 201, 0.85);
  outline-offset: 2px;
  border-radius: 4px;
}

/* Chat polish v12: universal section-page primitives. Apply across
   Workspaces / Agents / Integrations / MCP / Workflows / etc. */

/* Page header entrance (fade-up 8px over 360ms). One-shot via
   animation-fill-mode both so React keys keep the element stable
   across re-renders. */
@keyframes cf-page-enter-up {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.cf-page-header {
  animation: cf-page-enter-up 360ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

/* Grid stagger: each child of a .cf-grid-stagger gets a cf-page-
   enter-up animation with a small per-position delay. Capped at
   6 children of stagger so a 50-card grid does not take 3s. */
.cf-grid-stagger > * {
  animation: cf-page-enter-up 360ms cubic-bezier(0.16, 1, 0.3, 1) both;
  will-change: transform, opacity;
}
.cf-grid-stagger > *:nth-child(1) { animation-delay: 0ms; }
.cf-grid-stagger > *:nth-child(2) { animation-delay: 40ms; }
.cf-grid-stagger > *:nth-child(3) { animation-delay: 80ms; }
.cf-grid-stagger > *:nth-child(4) { animation-delay: 120ms; }
.cf-grid-stagger > *:nth-child(5) { animation-delay: 160ms; }
.cf-grid-stagger > *:nth-child(6) { animation-delay: 200ms; }
.cf-grid-stagger > *:nth-child(n+7) { animation-delay: 240ms; }

/* Card hover lift. translateY + soft shadow. Doesn't fight inline
   transition: all 0.2s because we target the same property
   (transform on hover; transition handles the interpolation). */
.cf-page-card {
  transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1),
              box-shadow 200ms ease,
              border-color 150ms ease !important;
}
.cf-page-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
}

/* Chat polish v17: modal entrance. Backdrop fade + panel scale-in. */
@keyframes cf-modal-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes cf-modal-scale-in {
  from { opacity: 0; transform: scale(0.94); }
  to   { opacity: 1; transform: scale(1); }
}
.cf-modal-backdrop {
  animation: cf-modal-fade-in 180ms ease-out both;
}
.cf-modal-panel {
  animation: cf-modal-scale-in 240ms cubic-bezier(0.16, 1, 0.3, 1) both;
  transform-origin: center center;
  will-change: transform, opacity;
}

/* Chat polish v18: side slide-over entrance for the WhatsNewPanel
   (and any future right-edge drawers). translateX from 100% so the
   panel slides in from off-screen-right. */
@keyframes cf-slide-in-right {
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
}
.cf-slide-in-right {
  animation: cf-slide-in-right 320ms cubic-bezier(0.16, 1, 0.3, 1) both;
  will-change: transform;
}

/* Chat polish v19: slide-in-from-top for banners (ChunkLoadBanner,
   etc.). Brief 280ms slide with a small overshoot via spring-curve. */
@keyframes cf-slide-in-top {
  from { opacity: 0; transform: translateY(-100%); }
  to   { opacity: 1; transform: translateY(0); }
}
.cf-slide-in-top {
  animation: cf-slide-in-top 280ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
  will-change: transform;
}

/* Chat polish v20: top-bar pill hover. Mirrors the sidebar + chat-row
   hover affordance -- background brightens to accent tint + border
   shifts to accent on hover. !important so it beats the inline
   background: S.inputBg + border: 1px solid S.border2 that every
   topbar pill carries. */
.cf-topbar-pill {
  transition: background 180ms ease, border-color 180ms ease,
              transform 180ms cubic-bezier(0.16, 1, 0.3, 1) !important;
}
.cf-topbar-pill:hover {
  background: rgba(91, 163, 201, 0.12) !important;
  border-color: rgba(91, 163, 201, 0.45) !important;
}

/* Chat polish v21: universal button micro-interactions. CSS-only --
   applies to every <button> in the app unless opted out with
   .cf-no-press class. */

/* v21a: click-press scale. Buttons subtly compress on mouse-down
   and spring back on release. Universal transition baseline added
   so transforms interpolate. */
button {
  transition: transform 110ms cubic-bezier(0.16, 1, 0.3, 1);
}
button:active:not(.cf-no-press):not([disabled]) {
  transform: scale(0.96);
}

/* v21b: opt-in icon-button hover rotate. Add .cf-icon-rotate to any
   <button> whose primary content is an icon and you want a subtle
   8deg tilt on hover. Doesn't affect text buttons. */
.cf-icon-rotate {
  transition: transform 220ms cubic-bezier(0.16, 1, 0.3, 1);
}
.cf-icon-rotate:hover {
  transform: rotate(8deg);
}

/* v21c: input field validation error shake. Add .cf-input-error
   (and remove after the animation) to shake an invalid input. */
@keyframes cf-input-shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-4px); }
  40%, 80% { transform: translateX(4px); }
}
.cf-input-error {
  animation: cf-input-shake 320ms ease-in-out both;
  border-color: rgba(239, 68, 68, 0.6) !important;
}

/* v21d: keyboard-focused button gets a slight scale-up alongside the
   :focus-visible ring. Helps the keyboard user see "this is the
   active button" beyond the outline. */
button:focus-visible:not([disabled]) {
  transform: scale(1.02);
}

/* v21e: accent-styled CTAs get a soft glow on hover. Detect via
   opt-in class .cf-cta. The glow uses box-shadow so it doesn't
   affect layout. */
.cf-cta {
  transition: transform 160ms cubic-bezier(0.16, 1, 0.3, 1),
              box-shadow 200ms ease;
}
.cf-cta:hover:not([disabled]) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(91, 163, 201, 0.32);
}

/* Chat polish v22: sidebar deeper polish */

/* v22a: sidebar chat-row title gets a subtle right-shift on row hover
   so it feels like it's stepping forward. The row already has an
   accent bg + icon scale from v5; this adds movement to the title. */
.cf-sidebar-row span {
  transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1);
}
.cf-sidebar-row:hover span {
  transform: translateX(2px);
}

/* v22b: sidebar row active accent bar (v5) gets a soft glow so it
   pops more in dark mode. */
.cf-sidebar-row[data-active="true"]::before {
  box-shadow: 0 0 8px rgba(91, 163, 201, 0.55);
}

/* v22c: search input (cf-input-shell class lives on the chat search
   bar in the sidebar; same focus glow as the main InputBar). Already
   covered by v3's .cf-input-shell rule -- this just makes sure the
   sidebar search has the class. Caller adds className. */

/* v22d: sidebar delete (X) button hover -- scale up + red tint
   beyond the existing opacity reveal. */
.cf-sidebar-row > button {
  transition: transform 160ms cubic-bezier(0.16, 1, 0.3, 1), color 140ms ease;
}
.cf-sidebar-row > button:hover {
  transform: scale(1.15);
}

/* v22e: ChatSideRow entrance stagger -- already done in v2. Here
   add a brief border-pulse when a NEW chat is added (cf-row-new
   opt-in class for a future "highlight just-created" use case). */
@keyframes cf-row-new-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(91, 163, 201, 0); }
  35%  { box-shadow: 0 0 0 4px rgba(91, 163, 201, 0.32); }
  100% { box-shadow: 0 0 0 0 rgba(91, 163, 201, 0); }
}
.cf-row-new {
  animation: cf-row-new-pulse 1.2s ease-out 1;
}

/* Chat polish v23: loading + empty + spinner polish */

/* v23a: skeleton shimmer for loading rows. Apply .cf-skeleton to a
   placeholder div; animates a gradient bg left-to-right. */
@keyframes cf-skeleton-shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
.cf-skeleton {
  background: linear-gradient(
    90deg,
    rgba(127,127,127,0.10) 0%,
    rgba(127,127,127,0.18) 50%,
    rgba(127,127,127,0.10) 100%
  );
  background-size: 200% 100%;
  animation: cf-skeleton-shimmer 1.4s linear infinite;
  border-radius: 6px;
}

/* v23b: empty-state container fade-in. Apply .cf-empty-state to any
   "No X yet" message so it doesn't pop in. */
.cf-empty-state {
  animation: cf-page-enter-up 360ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

/* v23c: spinner color cycles through accent / accent2 / accent. Apply
   .cf-spinner-color to any rotating element. */
@keyframes cf-spinner-hue {
  0%, 100% { color: rgba(91, 163, 201, 0.9); }
  50%      { color: rgba(91, 163, 201, 0.55); }
}
.cf-spinner-color {
  animation: cf-spinner-hue 1.6s ease-in-out infinite;
}

/* v23d: progress bar smooth width transition. Apply .cf-progress-bar
   to any element whose width animates (token meter, sync progress). */
.cf-progress-bar {
  transition: width 320ms cubic-bezier(0.16, 1, 0.3, 1);
}

/* v23e: badge "new" pulse. Decorative dot pulse for unread / new
   markers. Apply .cf-badge-pulse to any small badge / dot. */
@keyframes cf-badge-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.18); opacity: 0.78; }
}
.cf-badge-pulse {
  animation: cf-badge-pulse 1.8s ease-in-out infinite;
  will-change: transform;
}

/* Chat polish v24: misc ambient polish */
.cf-clickable {
  cursor: pointer;
  transition: filter 120ms ease;
}
.cf-clickable:active {
  filter: brightness(0.9);
}

@keyframes cf-bg-ambient {
  0%, 100% { background-position: 0% 0%; }
  50%      { background-position: 100% 50%; }
}
.cf-bg-ambient {
  background-image: radial-gradient(
    ellipse at top,
    rgba(91, 163, 201, 0.04) 0%,
    transparent 50%
  );
  background-size: 200% 200%;
  animation: cf-bg-ambient 16s ease-in-out infinite;
}

.cf-badge-hover {
  transition: transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
  display: inline-block;
}
.cf-badge-hover:hover {
  transform: scale(1.05);
}

.cf-divider-glow {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(127, 127, 127, 0.35) 50%,
    transparent 100%
  );
  transition: background 300ms ease;
}
.cf-divider-glow:hover {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(91, 163, 201, 0.55) 50%,
    transparent 100%
  );
}

[data-cf-img-loaded="false"] {
  opacity: 0;
  transform: scale(0.98);
}
[data-cf-img-loaded="true"] {
  opacity: 1;
  transform: scale(1);
  transition: opacity 280ms ease, transform 280ms cubic-bezier(0.16, 1, 0.3, 1);
}

@media (prefers-reduced-motion: reduce) {
  .cf-page-header                  { animation: none; }
  .cf-grid-stagger > *             { animation: none; }
  .cf-page-card:hover              { transform: none; box-shadow: none; }
  .cf-modal-backdrop               { animation: none; }
  .cf-modal-panel                  { animation: none; }
  .cf-slide-in-right               { animation: none; }
  .cf-slide-in-top                 { animation: none; }
  button:active:not(.cf-no-press):not([disabled]) { transform: none; }
  button:focus-visible:not([disabled])            { transform: none; }
  .cf-icon-rotate:hover            { transform: none; }
  .cf-input-error                  { animation: none; }
  .cf-cta:hover:not([disabled])    { transform: none; box-shadow: none; }
  .cf-sidebar-row:hover span       { transform: none; }
  .cf-sidebar-row > button:hover   { transform: none; }
  .cf-row-new                      { animation: none; }
  .cf-skeleton                     { animation: none; }
  .cf-empty-state                  { animation: none; }
  .cf-spinner-color                { animation: none; }
  .cf-progress-bar                 { transition: none; }
  .cf-badge-pulse                  { animation: none; }
  .cf-clickable                    { transition: none; }
  .cf-bg-ambient                   { animation: none; background-image: none; }
  .cf-badge-hover:hover            { transform: none; }
  .cf-divider-glow                 { transition: none; }
  [data-cf-img-loaded="true"]      { transition: none; }
}

@media (prefers-reduced-motion: reduce) {
  .cf-copy-btn[data-copied="true"] { animation: none; }
  .cf-copy-btn[data-copied="failed"] { animation: none; }
  [data-cf-avatar-pulse="true"]    { animation: none; }
  .cf-sidebar-row                  { animation: none; }
  .cf-hero-logo                    { animation: none; }
  .cf-hero-logo > *                { animation: none; }
  .cf-hero-logo:hover              { transform: none; box-shadow: none; }
  .cf-hero-logo:hover > *          { transform: none; }
  .cf-hero-logo::before            { animation: none; opacity: 0; }
  .cf-hero-title                   { animation: none; }
  .cf-hero-desc                    { animation: none; }
  .cf-hero-starter                 { animation: none; transition: none; }
  .cf-hero-starter:hover           { transform: none; }
  .cf-stream-accent::after         { animation: none; opacity: 0.4; }
  .cf-newchat-btn:hover            { transform: none; }
  .cf-sidebar-row:hover .cf-sidebar-icon { transform: none; }
  .cf-brand-logo:hover             { transform: none; }
  .cf-provider-badge:hover         { transform: none; }
  .cf-tool-chip[data-status="done"] { animation: none; }
  .cf-user-avatar:hover            { transform: none; }
  .cf-source-chip:hover            { transform: none; }
  .cf-attach-chip                  { animation: none; }
  .cf-attach-chip:hover            { transform: none; }
  .cf-send-btn:hover:not([data-busy="true"]) { transform: none; box-shadow: none; }
  .cf-mic-btn[data-dictating="true"] { animation: none; }
  .cf-mic-btn:hover:not([data-dictating="true"]) { transform: none; }
  .cf-toast                        { animation: none; }
  .cf-toast::after                 { animation: none; width: 0; }
  .cf-toast-dot                    { animation: none; }
  .cf-toast-action:hover           { transform: none; }
  .cf-toast-text                   { animation: none; }
  .cf-jump-pill[data-visible="true"] { animation: none; }
  .cf-jump-pill[data-visible="true"]:hover { transform: none; box-shadow: 0 4px 14px rgba(0,0,0,0.25); }
  .cf-jump-pill[data-visible="true"]:hover svg { animation: none; }
  .cf-chat-scroller                { scroll-behavior: auto; }
  .cf-md-heading::after            { transition: none; }
  .cf-md-img:hover                 { transform: none; }
  .cf-md-heading-anchor            { transition: none; }
  .cf-color-swatch:hover           { transform: none; }
  .cf-emoji-tile:hover             { transform: none; }
  .cf-spinner-icon                 { animation: none; }
  /* Hover-revealed code chips stay at 0.45/1 (no animation needed
     for the opacity step under reduced-motion -- already discrete). */
  .cf-auth-panel                   { animation: none; }
  .cf-auth-submit                  { transition: none; }
  .cf-auth-submit:hover            { transform: none; box-shadow: none; }
  .cf-auth-tab                     { transition: none; }
  .cf-auth-tab:hover               { background: none; }
  .cf-auth-sso                     { transition: none; }
  .cf-auth-sso:hover               { transform: none; }
  .cf-auth-spinner svg             { animation: none; }
  .cf-audit-row                    { transition: none; }
  .cf-audit-row:hover              { background: none; }
  .cf-upgrade-banner               { animation: none; }
  .cf-pricing-card                 { transition: none; }
  .cf-pricing-card:hover           { transform: none; box-shadow: none; }
  .cf-pricing-addon                { transition: none; }
  .cf-pricing-addon:hover          { transform: none; border-color: inherit; }
  .cf-pricing-toggle               { transition: none; }
  .cf-pricing-toggle[data-active="true"] { transform: none; }
  .cf-pricing-cta                  { transition: none; }
  .cf-pricing-cta:hover            { transform: none; box-shadow: none; }
  .cf-pricing-header               { animation: none; }
  .cf-owner-row                    { transition: none; }
  .cf-owner-row:hover              { background: none; }
  .cf-owner-expand                 { animation: none; }
  .cf-owner-empty                  { animation: none; }
  .cf-btn-lift                     { transition: none; }
  .cf-btn-lift:hover               { transform: none; box-shadow: none; filter: none; }
  .cf-plan-chip                    { transition: none; }
  .cf-plan-chip:hover              { transform: none; }
  .cf-empty-icon-pop               { animation: none; }
  html                             { scroll-behavior: auto; }
  .cf-pulse-dot                    { animation: none; }
  .cf-skel-card                    { animation: none; background: rgba(127,127,127,0.08) !important; }
  .cf-glow-hover:hover             { box-shadow: none; }
  .cf-wf-step-pulse                { animation: none; }
  .cf-wf-step-enter                { animation: none; }
  .cf-wf-progress-bar > div        { transition: none; }
  .cf-wf-card                      { transition: none; animation: none; }
  .cf-wf-card:hover                { transform: none; }
  .cf-wf-card-pulse                { animation: none; }
  .cf-wf-star                      { transition: none; }
  .cf-wf-star:hover                { transform: none; }
  .cf-toast-dismiss                { transition: none; }
  .cf-toast-dismiss:hover          { opacity: 1; }
  .cf-shimmer                      { animation: none; background: rgba(127,127,127,0.08) !important; }
  .cf-page-fade-in                 { animation: none; }
  .cf-composer-focus:focus         { box-shadow: none; }
  .cf-sidebar-slide:hover          { transform: none; }
  .cf-section-enter                { animation: none; }
  .cf-slide-up-modal               { animation: none; }
  .cf-loading-dots span            { animation: none; }
  .cf-grow-x:hover                 { transform: none; }
  .cf-link-arrow:hover::after      { transform: none; }
}
`;
  document.head.appendChild(s);
}

// Drop-in wrapper for any "<button onClick={copy then maybe toast}>"
// pattern. Preserves the caller's children unchanged (no icon swap
// to prevent width jumps in mid-bubble hover-revealed action bars).
//
// Optional copiedChildren prop swaps content during the 1.2s morph
// window -- useful for text-style buttons ("Copy" -> "Copied") where
// a width change is welcome confirmation. Icon-only buttons leave
// it unset; the green + pop is enough.
//
// Forwards onMouseEnter / onMouseLeave / style / className so caller
// hover handlers and palette-driven styling still work.
export function CopyMorphButton({
  text, title = "Copy", style, className, children, copiedChildren,
  failedChildren, onCopied, onMouseEnter, onMouseLeave, disabled,
}) {
  // 'idle' | 'copied' | 'failed' -- both terminal states auto-revert
  // to 'idle' after 1200ms. Rapid double-clicks reset the timer.
  const [copyState, setCopyState] = useState("idle");
  const timerRef = useRef(null);
  const handle = (e) => {
    try { e && e.stopPropagation && e.stopPropagation(); } catch {}
    if (!text || typeof navigator === "undefined" || !navigator.clipboard) {
      // No clipboard API -> show failed.
      setCopyState("failed");
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopyState("idle"), 1200);
      return;
    }
    navigator.clipboard.writeText(text).then(() => {
      setCopyState("copied");
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopyState("idle"), 1200);
      try { onCopied && onCopied(); } catch {}
    }).catch(() => {
      setCopyState("failed");
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopyState("idle"), 1200);
    });
  };
  const isCopied = copyState === "copied";
  const isFailed = copyState === "failed";
  return (
    <button
      type="button"
      className={"cf-copy-btn " + (className || "")}
      data-copied={isCopied ? "true" : (isFailed ? "failed" : "false")}
      aria-label={isCopied ? "Copied" : (isFailed ? "Copy failed" : title)}
      title={isCopied ? "Copied!" : (isFailed ? "Copy failed" : title)}
      onClick={handle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={disabled}
      style={style}
    >
      {isCopied && copiedChildren !== undefined ? copiedChildren :
       isFailed && failedChildren !== undefined ? failedChildren :
       children}
    </button>
  );
}

export default CopyMorphButton;
